import { useRef, useState } from "react";
import PaymentModal from "./PaymentModal";
import Paywall, { offerDiscountFor } from "./Paywall";
import NativePaywall from "./NativePaywall";
import { paywallsSuppressed } from "@/utils/paywallGate";
import { Capacitor } from "@capacitor/core";

// Drop-in replacement for <PaymentModal> that adds the exit-intent paywall:
// if the buyer closes the checkout without paying, a last-chance discounted
// offer for the same document is shown (once). Unlocking reopens the checkout
// at the offer price; dismissing closes everything. Data that never completes
// payment is untouched.
export default function CheckoutWithPaywall({
  docLabel, documentType, template, basePrice, discount = null,
  quantity = 1, onSuccess, onClose, previewImage = null, intentExtras = null,
}) {
  // The native app sells nothing itself (the Spotify model): the link-out
  // sheet replaces the whole Stripe flow there; the web keeps everything.
  const native = Capacitor.isNativePlatform?.() || false;

  const [showPaywall, setShowPaywall] = useState(false);
  const [offerPrice, setOfferPrice] = useState(null); // discounted price once unlocked via the paywall
  const [offerEnforced, setOfferEnforced] = useState(false); // server-enforced vs client discount
  const paidRef = useRef(false);
  const showPaywallRef = useRef(false); // synchronous mirror of showPaywall (guards double-taps)

  // Post-purchase upsell: the payment-success page stores the offer the
  // buyer accepted (targets + expiry); while it's live and covers this
  // document, checkout opens at the discounted price. Server-enforced — the
  // backend re-derives the discount from this IP's countdown window.
  const upsell = (() => {
    try {
      const raw = sessionStorage.getItem("ms:upsell");
      if (!raw) return null;
      const u = JSON.parse(raw);
      if (!u || !u.expiresAt || new Date(u.expiresAt) <= new Date()) return null;
      return Array.isArray(u.targets) && u.targets.includes(documentType) && u.percent > 0 ? u : null;
    } catch { return null; }
  })();
  const upsellPrice = upsell ? +(basePrice * (100 - upsell.percent) / 100).toFixed(2) : null;
  const upsellDiscount = upsellPrice != null && (!discount || discount.discountedPrice > upsellPrice)
    ? { discountedPrice: upsellPrice, upsell: true, discountPercent: upsell.percent }
    : null;

  // Reopen at the offer price: server-enforced offer mode when the backend
  // stamped a window, otherwise a plain client discount (charged like a coupon).
  const effectiveDiscount = offerPrice != null
    ? offerDiscountFor(offerPrice, offerEnforced, basePrice)
    : (upsellDiscount || discount);

  // Free orders (100%-off coupon) never need a discount pitch.
  const freeNow = (effectiveDiscount?.discountedPrice ?? basePrice) <= 0;

  const handleSuccess = async (args) => { paidRef.current = true; await onSuccess?.(args); };

  const handleModalClose = () => {
    // A double-tap on the X fires onClose twice before the modal unmounts —
    // ignore anything after we've decided to open the paywall.
    if (showPaywallRef.current) return;
    // Paid, free, or abandoning the already-discounted reopened checkout → close.
    // Subscribers with downloads left (and Enterprise plans always) get no pitch.
    if (paidRef.current || freeNow || offerPrice != null || paywallsSuppressed()) { onClose?.(); return; }
    // First abandon → show the last-chance paywall.
    showPaywallRef.current = true;
    setShowPaywall(true);
  };

  const unlock = (discountedPrice, serverEnforced) => {
    showPaywallRef.current = false;
    setOfferEnforced(!!serverEnforced);
    setOfferPrice(discountedPrice);
    setShowPaywall(false);
  };
  const dismissPaywall = () => { showPaywallRef.current = false; setShowPaywall(false); onClose?.(); };

  if (native) {
    return <NativePaywall mode="all" docLabel={docLabel} previewImage={previewImage} onClose={onClose} />;
  }

  return (
    <>
      {!showPaywall && (
        <PaymentModal
          docLabel={docLabel}
          documentType={documentType}
          template={template}
          basePrice={basePrice}
          discount={effectiveDiscount}
          quantity={quantity}
          intentExtras={intentExtras}
          onSuccess={handleSuccess}
          onClose={handleModalClose}
        />
      )}
      {showPaywall && (
        <Paywall
          docLabel={docLabel}
          documentType={documentType}
          basePrice={basePrice}
          previewImage={previewImage}
          onUnlock={unlock}
          onDismiss={dismissPaywall}
          checkout={{
            template, quantity, onSuccess: handleSuccess,
            onPaid: () => { showPaywallRef.current = false; setShowPaywall(false); onClose?.(); },
          }}
        />
      )}
    </>
  );
}
